import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createErrorHandler } from '@rjsf/utils';

import { Button, Icon } from '@iq/react-components';
import ListItem from '../../ListItem';
import {
  saveStateSet,
  getStateSets,
  deleteStateSet,
  updateStateSet,
} from '../../../bundles/statesets';

import Heading from '../../Heading';
import SimpleModal from '../../SimpleModal/SimpleModal';
import { schema, uiSchema } from './schemas/stateSetSchema';
import JsonEditor from '../../JsonEditor';
import {
  addRequiredFieldErrors,
  addRequiredObjectErrors,
  suppressNestedRequiredFieldErrors,
  suppressRequiredFieldErrors,
} from '../../JsonEditor/validationUtils';
import { getActiveSite } from '../../../bundles/sites';

const getTriggerText = (stateSet, trigger) => {
  if (!trigger?.condition) {
    // Only virtual state sets are triggered manually; component states always have a trigger.
    return stateSet.isVirtualStateSet ? 'Manual' : '';
  }
  if (trigger.condition === '<>') {
    return `${trigger.triggerValue1 ?? ''} <> ${trigger.triggerValue2 ?? ''}`;
  }
  return `${trigger.condition} ${trigger.triggerValue ?? ''}`;
};

const getStates = (stateSet) => {
  if (!stateSet.states) {
    return null;
  }
  const states = Array.isArray(stateSet.states) ? stateSet.states : [stateSet.states];

  return states.map((state, index) => [
    '',
    '',
    <div
      key={`state-name-${stateSet.id}-${index}`}
      className="state-name"
    >
      {state.name}
    </div>,
    <div
      key={`state-text-${stateSet.id}-${index}`}
      className="state-text"
    >
      {state.text}
    </div>,
    <div key={`state-color-${stateSet.id}-${index}`}>
      <div
        className="state-color"
        style={{ backgroundColor: state.color }}
      ></div>
      {state.color}
    </div>,
    <div key={`state-trigger-${stateSet.id}-${index}`}>
      {getTriggerText(stateSet, state.trigger)}
    </div>,
    <div
      key={`state-description-${stateSet.id}-${index}`}
      className="state-description"
    >
      {state.description}
    </div>,
  ]);
};

const [virtualStateSchema, componentStatesArraySchema] =
  schema.dependencies.isVirtualStateSet.oneOf.map(({ properties }) => properties.states);
const componentStateSchema = componentStatesArraySchema.items;
const triggerRule = schema.definitions.trigger.allOf[0];
const getTriggerSchema = (conditionalSchema) => ({
  ...conditionalSchema,
  properties: {
    ...schema.definitions.trigger.properties,
    ...conditionalSchema.properties,
  },
});
const betweenTriggerSchema = getTriggerSchema(triggerRule.then);
const singleTriggerSchema = getTriggerSchema(triggerRule.else);
const nestedRequiredSchemas = [
  virtualStateSchema,
  componentStateSchema,
  betweenTriggerSchema,
  singleTriggerSchema,
];

const customValidate = (formData, validationErrors) => {
  addRequiredFieldErrors(formData, validationErrors, schema);

  const hasNoStates = Array.isArray(formData.states)
    ? formData.states.length === 0
    : !formData.states;
  if (hasNoStates) {
    validationErrors.states ??= createErrorHandler();
    validationErrors.states.addError('At least one state is required.');
    return validationErrors;
  }

  const states = Array.isArray(formData.states) ? formData.states : [formData.states];

  states.forEach((state, stateIndex) => {
    const stateValidationErrors = Array.isArray(formData.states)
      ? validationErrors.states[stateIndex]
      : validationErrors.states;
    const stateSchema = formData.isVirtualStateSet ? virtualStateSchema : componentStateSchema;

    addRequiredObjectErrors(state, stateValidationErrors, stateSchema);

    if (!state.trigger) {
      return;
    }

    const triggerSchema =
      state.trigger.condition === '<>' ? betweenTriggerSchema : singleTriggerSchema;
    addRequiredObjectErrors(state.trigger, stateValidationErrors.trigger, triggerSchema);
  });

  return validationErrors;
};

const customTransformErrors = (validationErrors) => {
  let transformedErrors = suppressRequiredFieldErrors(validationErrors, schema);
  for (const nestedSchema of nestedRequiredSchemas) {
    transformedErrors = suppressNestedRequiredFieldErrors(transformedErrors, nestedSchema);
  }

  return transformedErrors.filter(
    (validationError) =>
      validationError.name !== 'if' &&
      validationError.name !== 'type' &&
      // Replaced by the "At least one state is required." message from customValidate
      validationError.name !== 'minItems'
  );
};

const StateSetView = memo(() => {
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({});

  const { id: siteId, org } = useSelector(getActiveSite);
  const stateSets = useSelector(getStateSets);

  const onFormChange = ({ formData: updatedFormData }) => {
    setFormData({ ...updatedFormData });
  };

  const handleUpdateState = (data) => {
    setShowModal(true);
    setFormData(data);
  };

  const onCancel = () => {
    setShowModal(false);
    setFormData({});
  };

  const onDelete = (stateSetId) => {
    dispatch(deleteStateSet(stateSetId, siteId));
  };

  const onSubmit = () => {
    const payload = { ...formData };
    if (payload.id) {
      dispatch(updateStateSet(payload));
      onCancel();
    } else {
      dispatch(saveStateSet(payload, siteId, org));
      onCancel();
    }
  };

  useEffect(() => {
    if (formData.isVirtualStateSet && Array.isArray(formData.states)) {
      setFormData((previousData) => ({ ...previousData, states: {} }));
    }
  }, [formData.isVirtualStateSet]);

  const headers = ['Name', 'Type', 'No. of states', 'Text', 'Color', 'Trigger', 'Suggestion'];

  const headerCols = headers.map((h) => (
    <div
      key={h}
      className="list-item--header__col"
    >
      {h.includes('Type') ? (
        <>
          <div className="stateset-type-info-trigger">
            {h}
            <Icon icon="he-help" />
          </div>
          <div className="stateset-type-info-tooltip--outer">
            <div className="stateset-type-info-tooltip--inner">
              <p>Stateset type cannot be changed after creation</p>
            </div>
          </div>
        </>
      ) : (
        h
      )}
    </div>
  ));

  return (
    <>
      <Heading contentRight={<Button onClick={() => setShowModal(true)}>Add state set</Button>} />

      <div className="manage-states-body">
        <div className="list-container">
          <ListItem
            isHeader
            columns={headerCols}
          />
          <div className="custom-thin-scrollbar">
            {stateSets?.map((s, index) => (
              <ListItem
                key={s.name + index}
                itemIndex={index}
                customEdit={true}
                onEdit={handleUpdateState}
                entity={`State set (${s.name})`}
                item={s}
                schema={schema}
                onDelete={() => onDelete(s.id)}
                deletePermission="statesets/Delete"
                permissionScope={{ org, site: siteId }}
                confirmationDialogTitle={'Delete stateSet'}
                confimationDialogBody={
                  <p style={{ paddingTop: '1.5rem' }}>
                    Are you sure you want to remove this stateset?
                  </p>
                }
                accordionContent={getStates(s)}
                columns={[
                  <div
                    key={`name-${s.id}`}
                    className="ellipsed-text"
                  >
                    {s.name}
                  </div>,
                  <div
                    key={`type-${s.id}`}
                    className="ellipsed-text"
                  >
                    {s.isVirtualStateSet === true ? 'Virtual' : 'Component'}
                  </div>,
                  <div
                    key={`length-${s.id}`}
                    className="count-indicator"
                  >
                    {s?.states?.length || 1}
                  </div>,
                  '',
                  '',
                  '',
                  '',
                ]}
              />
            ))}
          </div>
        </div>
      </div>
      {showModal && (
        <SimpleModal
          title={formData.id ? `Update State set (${formData?.name})` : 'Add state set'}
          onClose={onCancel}
        >
          <div>
            <JsonEditor
              schema={schema}
              uiSchema={uiSchema(formData.id)}
              formData={formData || {}}
              customValidate={customValidate}
              customTransformErrors={customTransformErrors}
              saveButtonText={formData.id ? 'Update' : 'Create'}
              onFormChange={onFormChange}
              initialEditMode
              showEditButton={false}
              cancelCallback={onCancel}
              onFormSubmit={onSubmit}
              editorOnly
            />
          </div>
        </SimpleModal>
      )}
    </>
  );
});

export default StateSetView;
